import React from "react";
import PropTypes from "prop-types";
import { Tabs, Accordion } from "@sunwing/wvi-components";
import { RCL as useTranslation } from "../../components/RCL";
import { FluidSections } from "../FluidSections";
import { getURLQueries } from "../../utils/getURLQueries";

const TabsAndAccordionConnector = ({ data }) => {
	const hashTag = getURLQueries().hash.replace("#", "");

	const _data = data.items.map(item => ({
		key: item.key,
		toggle: {
			icon: item.icon,
			label: {
				heading: item.heading,
				subHeading: item.subheading,
				tag: item.tag,
			},
			image: {
				data: item?.image?.[0].secure_url,
				alt: "",
			},
		},
		disabled: item.disabled,
		borderlessTop: item.accordionBorderlessTop,
		panel: <FluidSections data={item.content} useWrapper={false} />,
	}));

	const formatAccordionActiveKey = (key, type) => {
		if (!key) {
			return null;
		}
		if (type === "Single") {
			return key;
		} else if (type === "Multiple") {
			if (key.includes(",")) {
				return key.split(",");
			}

			return [key];
		}
	};

	const dictionary = {
		"next": useTranslation({ searchKey: "next" }),
		"previous": useTranslation({ searchKey: "previous" }),
		"nextTab": useTranslation({ searchKey: "go-to-next-tab" }),
		"previousTab": useTranslation({ searchKey: "go-to-prev-tab" }),
	};

	// Doing this because optionally rendering the button causes an issue with react children.
	if (data.type === "Accordion" && data.accordionExpandAll)
		return (
			<Accordion
				defaultValue={formatAccordionActiveKey(data.activeKey, data.accordionType)}
				collapsible={data.accordionCollapsible}
				type={data.accordionType.toLowerCase()}
				orientation={data.orientation.toLowerCase()}>
				<Accordion.ExpandCollapseButton />
				{_data?.map(tab => (
					<Accordion.Item
						key={tab.key}
						value={tab.key}
						disabled={tab.disabled}
						borderlessTop={tab.borderlessTop}>
						<Accordion.Trigger image={tab.toggle.image.data}>
							<Accordion.Label>{tab.toggle.label.heading}</Accordion.Label>
							<Accordion.Description>{tab.toggle.label.subHeading}</Accordion.Description>
						</Accordion.Trigger>
						<Accordion.Content>{tab.panel}</Accordion.Content>
					</Accordion.Item>
				))}
			</Accordion>
		);

	if (data.type === "Accordion")
		return (
			<Accordion
				defaultValue={formatAccordionActiveKey(hashTag || data.activeKey, data.accordionType)}
				collapsible={data.accordionCollapsible}
				type={data.accordionType.toLowerCase()}
				orientation={data.orientation.toLowerCase()}>
				{_data?.map(tab => (
					<Accordion.Item
						id={tab.key}
						key={tab.key}
						value={tab.key}
						disabled={tab.disabled}
						borderlessTop={tab.borderlessTop}>
						<Accordion.Trigger image={tab.toggle.image.data}>
							<Accordion.Label>{tab.toggle.label.heading}</Accordion.Label>
							<Accordion.Description>{tab.toggle.label.subHeading}</Accordion.Description>
						</Accordion.Trigger>
						<Accordion.Content>{tab.panel}</Accordion.Content>
					</Accordion.Item>
				))}
			</Accordion>
		);

	if (data.type === "Tabs")
		return (
			<Tabs
				defaultValue={data.activeKey}
				isSticky={data.tabsIsSticky}
				activationMode={data.tabsActivationMode.toLowerCase()}
				orientation={data.orientation.toLowerCase()}>
				<Tabs.List>
					{_data?.map(tab => {
						const key = tab?.key || null;
						return (
							<Tabs.Trigger key={key} value={key} icon={tab.toggle.icon}>
								{tab.toggle.label.heading}
							</Tabs.Trigger>
						);
					})}
				</Tabs.List>
				{_data?.map(tab => {
					const key = tab?.key || null;
					return (
						<Tabs.Content key={key} value={key}>
							{tab.panel}
						</Tabs.Content>
					);
				})}
				{data.tabsType === "Has Bookend" && (
					<Tabs.Bookend
						border
						leftTitle={dictionary["previous"]}
						leftSubHeading={dictionary["previousTab"]}
						rightTitle={dictionary["next"]}
						rightSubHeading={dictionary["nextTab"]}
					/>
				)}
			</Tabs>
		);
	return null;
};

TabsAndAccordionConnector.propTypes = {
	data: PropTypes.shape({
		activeKey: PropTypes.string,
		title: PropTypes.string,
		type: PropTypes.oneOf(["Tabs", "Accordion"]),
		accordionType: PropTypes.oneOf(["Single", "Multiple"]),
		accordionCollapsible: PropTypes.bool,
		orientation: PropTypes.oneOf(["Horizontal", "Vertical"]),
		accordionExpandAll: PropTypes.bool,
		items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
		tabsActivationMode: PropTypes.oneOf(["Automatic", "Manual"]),
		tabsType: PropTypes.oneOf(["Default", "Has Bookend"]),
		tabsIsSticky: PropTypes.bool,
	}).isRequired,
};

export { TabsAndAccordionConnector };
